/* eslint-disable react/prop-types */
import React, { useEffect, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { Box, Typography } from "@mui/material";
import { useParamsState } from "./useParamsState";
import axios from "axios";
import { api } from "../api/api";

export function useFetchWithPagination(
  url,
  options = { perPage: 10, run: true },
) {
  const { perPage, run } = options;

  const [loading, setLoading] = useState(true);
  const [page, setPage] = useParamsState("current_page", 1, "number");
  const [data, setData] = useState();
  const [pageCount, setPageCount] = useState(1);
  const [refetcher, setRefetcher] = useState(false);

  const fullUrl =
    url +
    (url?.includes("?") ? "&" : "?") +
    `limit=${perPage}&offset=${perPage * (page - 1)}`;

  // Reset to page 1 when URL changes
  useEffect(() => {
    if (!run || !url) return;
    if (page !== 1) setPage(1);
  }, [url]);

  useEffect(() => {
    if (!run || !url) return;

    const source = axios.CancelToken.source();
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await api.get(fullUrl, { cancelToken: source.token });
        setData(res.data);
        if (res.data?.count) {
          setPageCount(Math.ceil(res.data.count / perPage) || 1);
        }
      } catch (err) {
        console.error(err?.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      source.cancel();
    };
  }, [fullUrl, refetcher, run]);

  const refetch = () => {
    setRefetcher((v) => !v);
  };

  const prevPage = () => setPage(page === 1 ? page : page - 1);
  const nextPage = () => setPage(page === pageCount ? page : page + 1);

  const PageButton = ({ index }) => (
    <button
      // variant={index === page ? "contained" : "outlined"}
      // size="small"
      className={`border rounded-full p-4 h-10 w-10 flex justify-center items-center transition-all hover:bg-black hover:text-white ${index === page ? "bg-black" : "bg-white"} ${index !== page ? "" : "text-white"}`}
      onClick={() => setPage(index)}
    >
      {index}
    </button>
  );

  const pagination =
    page > pageCount || pageCount === 1 ? null : (
      <Box
        display="flex"
        flexDirection={{ xs: "column" }}
        alignItems={{ sm: "center" }}
        justifyContent={{ sm: "space-between" }}
        mt={2}
        gap={2}
      >
        <Box display="flex" gap={1} alignItems="center">
          {page > 2 && (
            <button
              // variant="outlined"
              // size="small"
              // style={{ border: "1px solid black", color: "black" }}
              className="border rounded-full p-4 h-10 w-10 flex justify-center items-center bg-white"
              onClick={() => setPage(1)}
            >
              <ChevronsLeft size={22} className="shrink-0" />
            </button>
          )}
          {page > 2 && (
            <button
              // variant="outlined"
              // size="small"
              onClick={prevPage}
              className="border rounded-full p-4 h-10 w-10 flex justify-center items-center bg-white"
              // style={{ border: "1px solid black", color: "black" }}
            >
              <ChevronLeft size={16} className="shrink-0" />
            </button>
          )}
          <PageButton index={1} />
          {page - 2 > 1 && (
            <Typography variant="body2" color="textSecondary">
              ...
            </Typography>
          )}
          {page - 1 > 1 && <PageButton index={page - 1} />}
          {page !== 1 && page !== pageCount && <PageButton index={page} />}
          {page + 1 < pageCount && <PageButton index={page + 1} />}
          {page + 2 < pageCount && (
            <Typography variant="body2" color="textSecondary">
              ...
            </Typography>
          )}
          {pageCount > 1 && <PageButton index={pageCount} />}
          {page + 1 < pageCount && (
            <button
              // variant="outlined"
              // size="small"
              onClick={nextPage}
              className="border rounded-full p-4 h-10 w-10 flex justify-center items-center bg-white"
              // style={{ border: "1px solid black", color: "black" }}
            >
              <ChevronRight size={16} className="shrink-0" />
            </button>
          )}
          {page + 1 < pageCount && (
            <button
              // variant="outlined"
              size="small"
              onClick={() => setPage(pageCount)}
              className="border rounded-full p-4 h-10 w-10 flex justify-center items-center bg-white"
              // style={{ border: "1px solid black", color: "black" }}
            >
              <ChevronsRight size={16} className="shrink-0" />
            </button>
          )}
        </Box>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 5 }}>
          Showing {perPage * (page - 1) + 1} to{" "}
          {Math.min(perPage * page, data?.count || 0)} of {data?.count} entries
        </Typography>
      </Box>
    );

  return {
    data,
    pagination,
    refetch,
    loading,
    setPage,
    page,
    pageCount,
  };
}
